import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StoreService } from '../../../../services/store.service';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { OrdersService } from '../../../../services/orders.service';
import {
  filter,
  forkJoin,
  map,
  Observable,
  Subscription,
  switchMap,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import {
  FilterBase,
  FilterDataPayload,
  FilterModel, OrderConfiguration,
  OrderData,
  OrderForm,
  OrderModel,
  OrderPayload
} from '../../../../model/order.model';
import { BaseModel, DateISO, UserModel } from '../../../../model/general.model';
import { userOptionLabel } from '../../../../utils/user.utils';
import { CellClassParams, ColDef } from '@ag-grid-community/core';
import { ORDER_TYPE, OrderType } from '../../../../config/order.config';
import { MatSelectChange } from '@angular/material/select';
import { TowerModel } from '../../../../model/tower.model';
import { SpanModel } from '../../../../model/span.model';
import { setColumnsDef } from '../../../../modules/data/utils/data.utils';
import { CHECKBOX_COLUMN } from '../../../../config/grid.config';
import {
  GridMasterComponent
} from '../../../../modules/shared/components/_grid/grid-master/grid-master.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ID_PARAM, ORDERS_URL } from '../../../../config/url.config';
import {
  arraysAreEqual,
  canCancelOrder
} from '../../../../utils/order.utils';

@Component({
  selector: 'app-new-order',
  templateUrl: './order-form.component.html',
  styleUrls: ['./order-form.component.scss']
})
export class OrderFormComponent implements OnInit, OnDestroy {

  subscription = new Subscription();
  allFilters: string[] = [];
  availableFilters: string[] = [];
  towers: TowerModel[] = [];
  spans: SpanModel[] = [];
  spansColDef: ColDef[] = [];
  towersColDef: ColDef[] = [];
  orderTypes = Object.keys(ORDER_TYPE);
  orderType: OrderType;
  towerForms: BaseModel[] = [];
  spanForms: BaseModel[] = [];
  selectedSpans: number[] = [];
  selectedTowers: number[] = [];
  externalTowerIds: number[] = [];
  externalSpanIds: number[] = [];
  users: UserModel[] = [];
  height = '400px';
  orderId: number;
  order: OrderModel;
  form: FormGroup<OrderForm> = this.formBuilder.group({
    name: new FormControl<string | null>(null, Validators.required),
    dataType: new FormControl<OrderType | null>(null, Validators.required),
    users: new FormControl<string[]>([], { nonNullable: true }),
    spanForm: new FormControl<number | null>(null),
    towerForm: new FormControl<number | null>(null),
    plannedExecutionDate: new FormControl<DateISO | null>(null),
    filters: this.formBuilder.array([])
  });

  @ViewChild('towersGrid') towersGrid!: GridMasterComponent;
  @ViewChild('spansGrid') spansGrid!: GridMasterComponent;

  get showTowersControls(): boolean {
    return this.orderType && this.orderType !== ORDER_TYPE.SPANS;
  };

  get showSpansControls(): boolean {
    return this.orderType && this.orderType !== ORDER_TYPE.TOWERS;
  };
  constructor(
    private store: StoreService,
    private formBuilder: FormBuilder,
    private ordersService: OrdersService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.orderId = this.route.snapshot.params[ID_PARAM]
    this.store.hideProjectSelector();
  }

  ngOnInit(): void {
    this.subscription.add(
      (this.form.controls.filters.valueChanges as Observable<FilterModel[]>)
        .subscribe(value => this.formValueChange(value))
    );

    this.orderId ? this.getOrderDetails() : this.getOrderConfig();
  }

  ngOnDestroy() {
    this.store.showProjectSelector();
    this.subscription.unsubscribe();
  }

  getOrderConfig(): void {
    this.store.showLoader();
    this.ordersService.getOrderConfig(this.store.selectedProject.id).pipe(
      tap((config) => {
        this.handleOrderConfig(config);
        this.store.hideLoader();
      })
    ).subscribe()
  }

  private handleOrderConfig(config: OrderConfiguration): void {
    this.users = config.users;
    this.spanForms = config.spanForms;
    this.towerForms = config.towerForms;
  }

  getOrderDetails(): void {
    if (this.orderId) {
      this.store.showLoader();

      forkJoin([
        this.ordersService.getOrderConfig(this.store.selectedProject.id),
        this.ordersService.getOrderById(this.orderId)
      ]).pipe(
        tap(([config, order]: [OrderConfiguration, OrderModel]) => {
          this.handleOrderConfig(config);
          this.order = order;
          this.store.updateSelectedProject(order.project.id)
          this.handleEditMode();

          if (this.order.pseFanOutInProgress) {
            this.store.showLoader();
            this.pollFanOutStatus();
          }
        })
      ).subscribe()
    }
  }

  handleEditMode(): void {

    if (this.order.hasSkmsDiscrepancy) {
      this.store.showErrorMsg('MISSING_DATA_IN_ORDER');
      this.store.hideLoader();
      return;
    }

    this.orderType = this.order.dataType;
    this.externalTowerIds = this.order.externalTowerIds;
    this.externalSpanIds = this.order.externalSpanIds;
    this.getFilters(false, this.order.filters);
    this.getData(this.order.filters, { towers: this.order.towers, spans: this.order.spans })

    this.form.patchValue({
      name: this.order.name,
      dataType: this.order.dataType,
      filters: this.order.filters,
      plannedExecutionDate: this.order.plannedExecutionDate,
      users: this.order.assignedTo.map(user => user.id),
      towerForm: this.order.towerForm?.id as number,
      spanForm: this.order.spanForm?.id as number
    }, { emitEvent: false });
  }

  private formValueChange(filters: FilterModel[]): void {
    if (filters.every(filter => filter.value)) {
      this.getData(filters);
    }
  }

  onSubmit() {
    if (this.form.valid) {
      this.store.showLoader();
      this.adjustForm();
      this.orderId ? this.updateOrder() : this.createOrder();
    }
  }

  private adjustForm(): void {
    if (this.form.value.dataType === 'SPANS') {
      this.form.controls['towerForm'].setValue(null);
    }

    if (this.form.value.dataType === 'TOWERS') {
      this.form.controls['spanForm'].setValue(null);
    }
  }

  private setFilters(filters: FilterBase[]): void {
    const requests = filters.map(param =>
      this.ordersService.getFilterValues(this.store.selectedProject.id, param.name, this.orderType).pipe(
        map(response => ({response, param}))
      )
    );

    forkJoin(requests).subscribe(
      results => {
        results.forEach(response => {
          this.availableFilters = this.availableFilters.filter(item => item !== response.param.name);
          this.filters.push(this.formBuilder.group({
            name: response.param.name,
            options: this.formBuilder.array(response.response),
            value: response.param.value
          }), {emitEvent: false});
          this.store.hideLoader();
        })
      },
    );
  }

  createOrder(): void {
    this.ordersService.saveOrder(this.prepareOrderPayload()).subscribe(() => this.handleSuccessResponse('ORDERS.CREATE_SUCCESS'))
  }

  updateOrder(): void {
    this.ordersService.updateOrder(this.orderId, this.prepareOrderPayload()).subscribe(() => this.handleSuccessResponse('ORDERS.UPDATE_SUCCESS'))
  }

  handleSuccessResponse(msgKey: string): void {
    void this.router.navigate([ORDERS_URL]);
    this.store.showSuccessMsg(msgKey);
  }

  getUserLabel(user: UserModel): string {
    return userOptionLabel(user);
  }

  get filters() {
    return this.form.get('filters') as FormArray;
  }

  addFilter(filter: string, value = '', emitEvent = true) {
    if (emitEvent) {
      this.store.showLoader();
    }
    this.ordersService.getFilterValues(this.store.selectedProject.id, filter, this.orderType).subscribe(options => {
      this.availableFilters = this.availableFilters.filter(item => item !== filter);
      this.filters.push(this.formBuilder.group({
        name: filter,
        options: this.formBuilder.array(options),
        value: value
      }), { emitEvent });

      if (emitEvent) {
        this.store.hideLoader();
      }
    })
  }

  removeFilter(index: number, name: string) {
    this.filters.removeAt(index);
    const filter = this.allFilters.find(item => item === name);

    this.form.markAsTouched();

    if (filter) {
      this.availableFilters.push(filter);
      this.sortFilters();
    }
  }

  private sortFilters() {
    this.availableFilters.sort((a, b) => a.localeCompare(b));
  }

  typeChanged(event: MatSelectChange) {
    this.store.showLoader();
    this.orderType = event.value;

    this.filters.clear({ emitEvent: false });
    this.getFilters(true, []);
  }

  private getFilters(fetchData = true, filters: FilterBase[]): void {
    this.ordersService.getDataFilters(this.store.selectedProject.id, this.orderType).subscribe(response => {
      this.allFilters = [...response];
      this.availableFilters = [...this.allFilters];
      this.sortFilters();

      if (fetchData) {
        this.getData([]);
      }

      if (filters.length) {
        this.setFilters(filters)
      } else {
        this.store.hideLoader();
      }
    })
  }

  isAddFilterDisabled() {
    const filters = this.form.value.filters as FilterModel[];
    return filters.length > 0 && filters.some(item => item.value === '');
  }

  getData(filters: FilterBase[], selectedRows?: OrderData): void {
    this.store.showLoader();
    this.ordersService.getDataByFilters(this.prepareFilterDataPayload(filters)).subscribe(data => {
      if (selectedRows) {
        const { spans, towers } = selectedRows;
        this.selectedSpans = [...spans.map(item => item.id), ...this.externalSpanIds];
        this.selectedTowers = [...towers.map(item => item.id), ...this.externalTowerIds];

      }
      this.towers = data.towers || [];
      this.towersColDef = setColumnsDef(this.towers);
      this.towersColDef.unshift(this.lockedCheckboxColumn(this.externalTowerIds));
      this.spans = data.spans || [];
      this.spansColDef = setColumnsDef(this.spans);
      this.spansColDef.unshift(this.lockedCheckboxColumn(this.externalSpanIds));
      this.store.hideLoader();
    })
  }

  private lockedCheckboxColumn(lockedIds: number[]): ColDef {
    return {
      ...CHECKBOX_COLUMN,
      cellClass: (params: CellClassParams) => lockedIds.includes(params.data?.id) ? 'locked-checkbox-cell' : ''
    };
  }

  private prepareFilterDataPayload(filters: FilterBase[]): FilterDataPayload {
    return {
      projectId: this.store.selectedProject.id,
      dataType: this.orderType,
      filters
    }
  }

  private prepareOrderPayload(): OrderPayload {
    const value = this.form.value;
    return {
      name: value.name,
      projectId: this.store.selectedProject.id,
      spanFormId: value.spanForm,
      towerFormId: value.towerForm,
      dataType: value.dataType as OrderType,
      filters: value.filters as FilterBase[],
      assignedTo: value.users,
      plannedExecutionDate: value.plannedExecutionDate,
      towers: this.getSelectedRowsIds(this.towersGrid ? this.towersGrid.selectedRows : []),
      spans: this.getSelectedRowsIds(this.spansGrid ? this.spansGrid.selectedRows : [])
    }
  }

  private getSelectedRowsIds(selectedRows: TowerModel[] | SpanModel[]): number[] {
    return selectedRows.map(item => item.id)
  }

  run() {
    if (!this.form.untouched || this.selectedSpansOrTowersChanged()) {
      this.store.showWarningMsg('ORDERS.UNSAVED_CHANGES');
      return;
    }

    this.store.showLoader();
    this.ordersService.runOrder(this.orderId).pipe(
      tap(() => {
        this.handleSuccessResponse('ORDERS.RUN_SUCCESS');
      })
    ).subscribe()
  }

  private selectedSpansOrTowersChanged(): boolean {
    return !arraysAreEqual(this.selectedTowers, this.getSelectedRowsIds(this.towersGrid ? this.towersGrid.selectedRows : [])) || !arraysAreEqual(this.selectedSpans, this.getSelectedRowsIds(this.spansGrid ? this.spansGrid.selectedRows : []));
  }

  cancel() {
    this.store.showLoader();
    this.ordersService.cancelOrder(this.orderId).pipe(
      tap(() => this.handleSuccessResponse('ORDERS.CANCEL_SUCCESS'))
    ).subscribe()
  }

  canCancel(): boolean {
    if (this.order) {
      return !canCancelOrder(this.order, this.store.isAuthor(this.order.createdBy.id))
    }

    return false;
  }

  readyToRun() {
    if (!this.order) {
      return false;
    }

    return this.order.readyToExecute;
  }

  protected searchMissingSKMS(): void {
    this.store.showLoader();
    this.subscription.add(
      this.ordersService.searchMisingSKMS(this.orderId).pipe(
        tap(() => {
          this.order.pseFanOutInProgress = true;
          this.pollFanOutStatus();
        })
      ).subscribe()
    );
  }

  private pollFanOutStatus(): void {
    this.store.showWarningMsg('ORDERS.FAN_OUT_IN_PROGRESS')
    this.subscription.add(
      timer(10000, 10000).pipe(
        switchMap(() => this.ordersService.getOrderById(this.orderId)),
        tap(order => this.order = order),
        takeWhile(order => order.pseFanOutInProgress, true),
        filter(order => !order.pseFanOutInProgress),
        switchMap(() => this.ordersService.getOrderConfig(this.store.selectedProject.id)),
        tap(config => {
          this.handleOrderConfig(config);
          this.handleEditMode();
          this.store.hideLoader();
        })
      ).subscribe()
    );
  }
}
